/**
 * ModalSheet — einheitlicher Drawer/Dialog (Story 0.3, AC-4)
 *
 * Verwendung: Payout-Details, Upgrade-/Checkout-Flows, Bestaetigungen.
 * `variant="dialog"` zentriert (Desktop) bzw. sitzt als Bottom-Sheet auf
 * Mobil; `variant="drawer"` faehrt rechts als Seitenpanel ein.
 *
 * a11y:
 *  - role="dialog" + aria-modal, Titel via aria-labelledby, optionale
 *    Beschreibung via aria-describedby.
 *  - Fokus wandert beim Oeffnen in den Dialog (initialFocusRef oder Panel)
 *    und beim Schliessen zurueck auf das ausloesende Element.
 *  - Fokusfalle: Tab/Shift+Tab zirkulieren innerhalb des Dialogs. Sitzt der
 *    Fokus AUSSERHALB des Panels (z. B. auf <body>, nachdem ein Klick auf das
 *    Overlay den Fokus verworfen hat), holt ihn schon der naechste Tab zurueck;
 *    ein focusin-Wachhund faengt zusaetzlich Fokuswechsel ein, die ohne Tab
 *    entstehen (Mausklick ins Hintergrunddokument, programmatischer focus()).
 *  - Escape schliesst (abschaltbar), Klick auf das Overlay schliesst
 *    (abschaltbar) — Klicks im Panel schliessen nie.
 *
 * Layering (Welle-2-Review F2): Ueber dem Sheet koennen weitere Ebenen liegen
 * (Command-Palette, verschachteltes Sheet). Escape darf immer nur die OBERSTE
 * Ebene schliessen. Umgesetzt ueber einen Modul-Stack: nur das zuletzt
 * geoeffnete Sheet reagiert. Fremde Ebenen (die nicht im Stack stehen, z. B.
 * die Command-Palette) werden am Event-Target erkannt — liegt es in einem
 * anderen role="dialog", ignorieren Escape UND Fokuswaechter das Ereignis und
 * lassen es zur oberen Ebene durch. Deshalb wird das Escape-Event bewusst NICHT
 * mehr per stopPropagation abgefangen: das blockierte sonst die Palette.
 *  - Body-Scroll wird waehrend der Anzeige gesperrt und exakt auf den
 *    vorherigen Wert zurueckgesetzt.
 */
import { useCallback, useEffect, useId, useRef, type ReactNode, type RefObject } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import './ui.css'

export type ModalSheetProps = {
  open: boolean
  onClose: () => void
  title: ReactNode
  description?: ReactNode
  children: ReactNode
  footer?: ReactNode
  variant?: 'dialog' | 'drawer'
  size?: 'sm' | 'md' | 'lg'
  /** Element, das nach dem Oeffnen den Fokus erhaelt. */
  initialFocusRef?: RefObject<HTMLElement | null>
  closeOnEscape?: boolean
  closeOnBackdrop?: boolean
  /** Blendet den Schliessen-Button aus (z. B. erzwungene Entscheidung). */
  hideCloseButton?: boolean
  className?: string
  'data-testid'?: string
}

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'

/**
 * Stack der offenen Sheets (aeusserstes zuerst). Nur das oberste Element
 * reagiert auf Escape/Fokusfalle — verschachtelte Sheets schliessen sonst
 * gemeinsam.
 */
const layerStack: symbol[] = []

/**
 * True, wenn das Event-Ziel in einer FREMDEN Dialog-Ebene liegt (Command-
 * Palette, Browser-eigene Dialoge o. ae.), also nicht im eigenen Panel.
 * Solche Ereignisse gehoeren der oberen Ebene und werden hier durchgelassen.
 */
function inForeignLayer(target: EventTarget | null, panel: HTMLElement | null): boolean {
  if (!panel || !(target instanceof Element)) return false
  if (panel.contains(target)) return false
  const dialog = target.closest('[role="dialog"], [role="alertdialog"]')
  return dialog != null && dialog !== panel && !panel.contains(dialog)
}

export function ModalSheet({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  variant = 'dialog',
  size = 'md',
  initialFocusRef,
  closeOnEscape = true,
  closeOnBackdrop = true,
  hideCloseButton = false,
  className = '',
  'data-testid': testId = 'modal-sheet',
}: ModalSheetProps) {
  const { t } = useTranslation('ui')
  const rawId = useId().replace(/:/g, '')
  const titleId = `ui-modal-title-${rawId}`
  const descId = `ui-modal-desc-${rawId}`
  const panelRef = useRef<HTMLDivElement>(null)
  const previouslyFocused = useRef<HTMLElement | null>(null)

  const focusables = useCallback((): HTMLElement[] => {
    const panel = panelRef.current
    if (!panel) return []
    // aria-hidden-Teilbaeume ausschliessen; auf Layout-Sichtbarkeit wird
    // bewusst nicht geprueft (in jsdom liefert offsetParent immer null).
    return Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
      (el) => !el.closest('[aria-hidden="true"]')
    )
  }, [])

  // Body-Scroll sperren
  useEffect(() => {
    if (!open) return
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [open])

  // Layer-Registrierung: solange offen, steht dieses Sheet im Stack.
  const layerId = useRef<symbol | null>(null)
  if (layerId.current === null) layerId.current = Symbol('modal-sheet')
  useEffect(() => {
    if (!open) return
    const id = layerId.current
    if (!id) return
    layerStack.push(id)
    return () => {
      const idx = layerStack.lastIndexOf(id)
      if (idx >= 0) layerStack.splice(idx, 1)
    }
  }, [open])

  const isTopLayer = useCallback(
    () => layerStack[layerStack.length - 1] === layerId.current,
    []
  )

  // Escape + Fokusfalle
  useEffect(() => {
    if (!open) return
    function onKeyDown(event: KeyboardEvent) {
      // Nur die oberste Sheet-Ebene reagiert; Ereignisse aus einer fremden
      // Ebene (Command-Palette) gehoeren dieser und werden durchgelassen.
      if (!isTopLayer() || inForeignLayer(event.target, panelRef.current)) return

      if (event.key === 'Escape') {
        if (!closeOnEscape) return
        // KEIN stopPropagation: eine hoeher liegende Ebene muss ihr Escape
        // ebenfalls sehen koennen (Welle-2-Review F2).
        onClose()
        return
      }
      if (event.key !== 'Tab') return
      const items = focusables()
      if (items.length === 0) {
        event.preventDefault()
        panelRef.current?.focus()
        return
      }
      const first = items[0]
      const last = items[items.length - 1]
      const active = document.activeElement as HTMLElement | null
      const outside = !panelRef.current?.contains(active)
      if (event.shiftKey && (active === first || outside)) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && (active === last || outside)) {
        // Vorwaertsfall aus dem Nichts (Fokus auf <body> nach Overlay-Klick):
        // frueher fiel der Fokus in die Seite hinter dem Dialog.
        event.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', onKeyDown, true)
    return () => document.removeEventListener('keydown', onKeyDown, true)
  }, [open, closeOnEscape, onClose, focusables, isTopLayer])

  // Fokus-Wachhund: Fokuswechsel ohne Tab (Mausklick hinter den Dialog,
  // programmatischer focus()) zurueck ins Panel holen.
  useEffect(() => {
    if (!open) return
    function onFocusIn(event: FocusEvent) {
      const panel = panelRef.current
      if (!panel || !isTopLayer()) return
      const target = event.target as Node | null
      if (target && panel.contains(target)) return
      if (inForeignLayer(event.target, panel)) return
      const items = focusables()
      ;(items[0] ?? panel).focus()
    }
    document.addEventListener('focusin', onFocusIn, true)
    return () => document.removeEventListener('focusin', onFocusIn, true)
  }, [open, focusables, isTopLayer])

  // Fokus setzen/zurueckgeben.
  // BEWUSST als LETZTER Effekt deklariert: React raeumt Effekte in
  // Deklarationsreihenfolge ab, dadurch ist der focusin-Wachhund beim
  // Zurueckgeben des Fokus bereits abgemeldet — sonst zoege er den Fokus im
  // Moment des Schliessens noch einmal ins (gleich entfernte) Panel.
  useEffect(() => {
    if (!open) return
    previouslyFocused.current = document.activeElement as HTMLElement | null
    const target = initialFocusRef?.current ?? focusables()[0] ?? panelRef.current
    target?.focus()
    return () => {
      previouslyFocused.current?.focus?.()
    }
  }, [open, initialFocusRef, focusables])

  if (!open) return null

  return createPortal(
    <div
      className={`ui-modal-overlay ui-modal-overlay--${variant}`}
      data-testid={`${testId}-overlay`}
      onMouseDown={(event) => {
        // Nur echte Klicks auf das Overlay selbst schliessen — mousedown statt
        // click, damit ein im Panel begonnener Drag nicht versehentlich
        // schliesst, wenn die Maus ueber dem Overlay losgelassen wird.
        if (closeOnBackdrop && event.target === event.currentTarget) onClose()
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descId : undefined}
        tabIndex={-1}
        className={`ui-modal ui-modal--${variant} ui-modal--${size} ${className}`.trim()}
        data-testid={testId}
        data-variant={variant}
      >
        <div className="ui-modal__header">
          <div className="ui-modal__titles">
            <h2 className="ui-modal__title" id={titleId}>
              {title}
            </h2>
            {description && (
              <p className="ui-modal__description" id={descId}>
                {description}
              </p>
            )}
          </div>
          {!hideCloseButton && (
            <button
              type="button"
              className="ui-modal__close"
              onClick={onClose}
              aria-label={t('modalSheet.close')}
              data-testid={`${testId}-close`}
            >
              <X aria-hidden="true" size={16} />
            </button>
          )}
        </div>

        <div className="ui-modal__body">{children}</div>

        {footer && <div className="ui-modal__footer">{footer}</div>}
      </div>
    </div>,
    document.body
  )
}
